import { computed, ref } from 'vue'

import { useToast } from 'primevue/usetoast'
import { NabooError } from '@/core/infrastructure/services/NabooError.ts'

// Types communs
export type LoadingState = {
  [key: string]: boolean
}

export type ErrorState = {
  hasError: boolean
  message: string
  code?: string
  details?: any
}

export type ToastSeverity = 'success' | 'info' | 'warn' | 'error' | 'secondary' | 'contrast'

export type ToastOptions = {
  severity: string
  summary: string
  detail: string
  life?: number
  group?: string
}

/**
 * Crée une base commune pour les stores Pinia
 * @param initialLoadingKeys Clés initiales pour l'état de chargement
 */
export function createStoreBase(initialLoadingKeys: string[] = []) {
  // SERVICES
  const toast = useToast()

  // Création de l'état de chargement initial
  const initialLoadingState: LoadingState = {}
  initialLoadingKeys.forEach((key) => {
    initialLoadingState[key] = false
  })

  // État
  const loading = ref<LoadingState>({ ...initialLoadingState })
  const error = ref<ErrorState>({
    hasError: false,
    message: '',
    code: undefined,
    details: undefined
  })
  const lastUpdated = ref<Date | null>(null)

  // Méthodes
  /**
   * Réinitialise l'état d'erreur
   */
  function resetError(): void {
    error.value = {
      hasError: false,
      message: '',
      code: undefined,
      details: undefined
    }
  }

  /**
   * Configure l'état d'erreur
   */
  function setError(err: any): void {
    error.value.hasError = true

    if (err instanceof NabooError) {
      error.value.message = err.message
      // Récupérer le code et les détails si disponibles
      if (err['code']) error.value.code = err['code']
      if (err['message']) error.value.details = err['message']
    } else if (err instanceof Error) {
      error.value.message = err.message
    } else {
      error.value.message = 'Une erreur inconnue est survenue'
    }
  }

  /**
   * Générique pour afficher un toast
   * @param options
   */
  function showToast(options: ToastOptions) {
    toast.add({
      severity: options.severity,
      summary: options.summary,
      detail: options.detail,
      life: options.life,
      group: options.group
    })
  }

  function showSuccessToast(summary: string, detail: string, life: number = 5000, sticky: boolean = false): void {
    if (sticky) toast.add({ severity: 'success',  summary, detail })
    else {
      toast.add({
        severity: 'success',
        summary,
        detail,
        life
      })
    }
  }


  /**
   * Affiche un message d'information
   */
  function showInfoToast(summary: string, detail: string, life: number = 5000, sticky: boolean = false): void {
    if (sticky) toast.add({ severity: 'info',  summary, detail })
    else {
      toast.add({
        severity: 'info',
        summary,
        detail,
        life
      })
    }
  }


  /**
   * Affiche un message d'erreur
   */
  function showErrorToast(summary: string, detail: string, life: number = 5000, sticky: boolean = false): void {
    if (sticky) toast.add({ severity: 'error',  summary, detail })
    else {
      toast.add({
        severity: 'error',
        summary,
        detail,
        life
      })
    }

  }

  /**
   * Met à jour la date de dernière mise à jour
   */
  function updateLastUpdated(): void {
    lastUpdated.value = new Date()
  }

  /**
   * Vérifie si les données ont besoin d'être rafraîchies
   * @param maxAgeMinutes Âge maximum des données en minutes
   */
  function shouldRefreshData(maxAgeMinutes: number = 5): boolean {
    if (!lastUpdated.value) return true

    const maxAgeMs = maxAgeMinutes * 60 * 1000
    return Date.now() - lastUpdated.value.getTime() > maxAgeMs
  }

  /**
   * Vérifie si un état de chargement est actif
   */
  const isLoading = computed(() => Object.values(loading.value).some(Boolean))

  /**
   * Réinitialise l'état du store
   */
  function resetBase(): void {
    // Réinitialiser l'état de chargement
    Object.keys(loading.value).forEach((key) => {
      loading.value[key] = false
    })

    resetError()
    lastUpdated.value = null
  }

  return {
    // État
    loading,
    error,
    lastUpdated,

    // Méthodes
    resetError,
    setError,
    updateLastUpdated,
    shouldRefreshData,
    resetBase,
    showToast,
    showSuccessToast,
    showInfoToast,
    showErrorToast,

    // Getters
    isLoading
  }
}
